import { Controller } from "@hotwired/stimulus"

// Per-row product search for invoice item fields.
// Fetches products from /products/search?q=... and fills in item fields on selection.
export default class extends Controller {
  static targets = ["input", "results", "productId", "unitPrice", "taxRate", "saleType", "saleTypeHidden", "hsCode", "uom", "retailPrice", "rateDesc", "rateDisplay", "sroSchedule", "sroItemSerial", "packSection", "packSelect", "packagingName", "unitsPerPack"]

  connect() {
    this._debounce = null
    this._onClickOutside = this._handleClickOutside.bind(this)
    document.addEventListener("click", this._onClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    clearTimeout(this._debounce)
  }

  search() {
    clearTimeout(this._debounce)
    const q = this.inputTarget.value.trim()
    if (q.length === 0) {
      this._hideResults()
      return
    }
    this._debounce = setTimeout(() => this._fetch(q), 250)
  }

  async _fetch(q) {
    try {
      const res = await fetch(`/products/search?q=${encodeURIComponent(q)}`, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })
      const products = await res.json()
      this._renderResults(products)
    } catch {
      this._hideResults()
    }
  }

  _renderResults(products) {
    if (products.length === 0) {
      this.resultsTarget.innerHTML = `
        <p class="px-3 py-2 text-sm text-slate-400 italic">No products found</p>
      `
      this.resultsTarget.classList.remove("hidden")
      return
    }

    this.resultsTarget.innerHTML = products.map(p => {
      const hsBadge = p.hs_code
        ? `<span class="shrink-0 inline-flex items-center rounded bg-primary/10 text-primary font-mono font-semibold text-[11px] px-1.5 py-0.5 tabular-nums">${this._escapeHtml(p.hs_code)}</span>`
        : `<span class="shrink-0 text-[11px] text-slate-400 italic">no HS</span>`
      return `
      <button type="button"
              class="w-full text-left px-3 py-2.5 hover:bg-primary/5 active:bg-primary/10 transition-colors flex items-center gap-2"
              data-action="click->product-search#select"
              data-product="${this._escape(JSON.stringify(p))}">
        ${hsBadge}
        <span class="text-sm font-medium text-slate-800 dark:text-slate-200 truncate flex-1 min-w-0">${this._escapeHtml(p.name)}</span>
        <span class="text-xs text-slate-400 shrink-0">PKR ${parseFloat(p.unit_price).toLocaleString("en-PK", { minimumFractionDigits: 2 })}</span>
      </button>`
    }).join("")

    this.resultsTarget.classList.remove("hidden")
  }

  select(event) {
    const product = JSON.parse(event.currentTarget.dataset.product)

    // Fill the search/name input
    this.inputTarget.value = product.name

    // Fill hidden and other fields
    if (this.hasProductIdTarget)   this.productIdTarget.value   = product.id
    if (this.hasUnitPriceTarget)   this.unitPriceTarget.value   = product.unit_price
    if (this.hasTaxRateTarget)     this.taxRateTarget.value     = product.tax_rate
    if (this.hasSaleTypeTarget)    this.saleTypeTarget.value    = product.sale_type
    if (this.hasSaleTypeHiddenTarget) this.saleTypeHiddenTarget.value = product.sale_type || ""
    if (this.hasHsCodeTarget)      this.hsCodeTarget.value      = product.hs_code || ""
    if (this.hasUomTarget)         this.uomTarget.value         = product.uom || ""
    if (this.hasRetailPriceTarget) this.retailPriceTarget.value = product.retail_price
    if (this.hasRateDescTarget)    this.rateDescTarget.value    = product.rate_desc || ""
    if (this.hasRateDisplayTarget) {
      const desc    = (product.rate_desc || "").trim()
      const percent = parseFloat(product.tax_rate)
      this.rateDisplayTarget.value =
        desc !== ""           ? desc :
        percent > 0           ? `${percent}%` :
                                "—"
    }
    if (this.hasSroScheduleTarget) this.sroScheduleTarget.value = product.sro_schedule_no || ""
    if (this.hasSroItemSerialTarget) this.sroItemSerialTarget.value = product.sro_item_serial_no || ""

    // Rebuilt for the product just chosen, and reset to its base unit: the pack list
    // belongs to the product, so whatever was picked for the previous one is gone.
    this._renderPackOptions(product)

    this._hideResults()

    // Tell the parent invoice-form controller to recalculate
    this.element.dispatchEvent(new CustomEvent("invoice-form:recalculate", { bubbles: true }))
  }

  // ── Packs ──────────────────────────────────────────────────────────────────
  //
  // A pack is a multiple of the product's own unit ("Box" = 12 singles). The line is
  // billed and printed in the pack; stock and the FBR payload are worked out from the
  // base unit on the server. All this has to do is record which pack was picked and
  // keep the price consistent with it.

  _renderPackOptions(product) {
    if (!this.hasPackSelectTarget) return

    const base  = product.uom_display || product.uom || "Unit"
    const packs = Array.isArray(product.packings) ? product.packings : []

    this.packSelectTarget.innerHTML =
      [`<option value="1" data-pack-name="">${this._escapeHtml(base)}</option>`]
        .concat(packs.map(pack =>
          `<option value="${pack.units_per_pack}" data-pack-name="${this._escapeHtml(pack.name)}"` +
          ` data-pack-price="${pack.priced ? pack.unit_price : ""}">` +
          `${this._escapeHtml(pack.name)} (${pack.units_per_pack} ${this._escapeHtml(base)})</option>`
        )).join("")

    this.packSelectTarget.value = "1"
    this.packSelectTarget.dataset.currentPack = "1"
    if (this.hasPackagingNameTarget) this.packagingNameTarget.value = ""
    if (this.hasUnitsPerPackTarget)  this.unitsPerPackTarget.value  = "1"

    // Nothing to choose between when the product has no packs.
    if (this.hasPackSectionTarget) {
      this.packSectionTarget.classList.toggle("hidden", packs.length === 0)
    }
  }

  onPackChange() {
    const option  = this.packSelectTarget.selectedOptions[0]
    if (!option) return

    const newPack = parseFloat(option.value) || 1
    const oldPack = parseFloat(this.packSelectTarget.dataset.currentPack) || 1

    if (this.hasPackagingNameTarget) this.packagingNameTarget.value = option.dataset.packName || ""
    if (this.hasUnitsPerPackTarget)  this.unitsPerPackTarget.value  = newPack

    // A pack priced on its own terms wins outright — that price is the reason it was
    // given one, and a gross sold at a bulk discount is not 144 singles' worth.
    //
    // Without one, rescale off the price currently in the box rather than the product's
    // list price, so a figure the user negotiated is carried across the switch instead
    // of being reset.
    const packPrice = parseFloat(option.dataset.packPrice)
    if (this.hasUnitPriceTarget) {
      if (!isNaN(packPrice)) {
        this.unitPriceTarget.value = packPrice.toFixed(2)
      } else if (oldPack > 0) {
        const current = parseFloat(this.unitPriceTarget.value)
        if (!isNaN(current)) {
          this.unitPriceTarget.value = (current / oldPack * newPack).toFixed(2)
        }
      }
    }

    this.packSelectTarget.dataset.currentPack = newPack
    this.element.dispatchEvent(new CustomEvent("invoice-form:recalculate", { bubbles: true }))
  }

  _hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  _handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this._hideResults()
    }
  }

  _escape(str) {
    return str.replace(/"/g, "&quot;")
  }

  _escapeHtml(str) {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
